import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

const API_BASE = API_ORIGIN;

// Same toggle-switch control EditUserForm.jsx / ProfilePage.jsx use for
// Account Status/Access Status -- kept as its own small copy here (no
// shared components folder for it yet) so all three pages look identical.
function ToggleSwitch({ checked, disabled, onChange, label, required }) {
  return (
    <div className="d-flex align-items-center gap-2">
      <label className={`profile-toggle${disabled ? " profile-toggle-disabled" : ""}`}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
        />
        <span className="profile-toggle-slider" />
      </label>
      <span className={disabled ? "text-muted" : "fw-semibold text-dark"}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
    </div>
  );
}

export default function UserCreateForm() {
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [companyId, setCompanyId] = useState("");
  // Access Status: the admin's own Enable/Disable decision -- separate
  // from Account Status below, and the one login() actually checks to
  // decide whether this account is allowed to sign in at all.
  const [isEnabled, setIsEnabled] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  useEffect(() => {
    authFetch(`${API_BASE}/roles`)
      .then((res) => res.json())
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch(() => setRoles([]));

    authFetch(`${API_BASE}/departments`)
      .then((res) => res.json())
      .then((data) => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => setDepartments([]));

    authFetch(`${API_BASE}/companies`)
      .then((res) => res.json())
      .then((data) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setCompanies([]));
  }, []);

  const resetForm = () => {
    setUserName("");
    setEmail("");
    setRoleId("");
    setMobileNo("");
    setDepartmentId("");
    setCompanyId("");
    setIsEnabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!userName.trim() || !email.trim() || !roleId) {
      setMessage({ type: "error", text: "Please fill in User Name, Email and Role." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          UserName: userName.trim(),
          Email: email.trim(),
          RoleID: roleId,
          MobileNo: mobileNo.trim() || null,
          DepartmentID: departmentId || null,
          CompanyID: companyId || null,
          IsEnabled: isEnabled,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to create user." });
        return;
      }

      // The backend still returns 201 (the user row was created either
      // way) even when the activation email itself failed to send -- it
      // flags that case with a `mail_error` field so the admin can see
      // the account exists but resend/investigate, rather than reading
      // a green "success" banner that says "...failed to send."
      setMessage({
        type: data.mail_error ? "error" : "success",
        text:
          data.message ||
          "User created. An activation email has been sent so they can set their password.",
      });
      if (!data.mail_error) {
        resetForm();
      }
    } catch (err) {
      setMessage({ type: "error", text: "Network error while creating the user." });
    } finally {
      setSubmitting(false);
    }
  };

  // Restyled to match EditUserForm.jsx's "User Details" card exactly --
  // page-title-head heading row, photo circle (initials derived live from
  // whatever's typed into Name so far, "?" until then), green field
  // labels in a row g-3 / col-md-6 grid, and Account Status/Access Status
  // shown as the same toggle switches, instead of the old plain
  // container/select-based layout. Per request, this page now looks the
  // same as Edit User. Account Status stays a disabled toggle (always
  // off) -- new users always start Inactive, same rule the old disabled
  // <select> enforced.
  const initials = userName.trim() ? userName.trim()[0].toUpperCase() : "?";

  return (
    <>
      <div className="d-flex align-items-center justify-content-between page-title-head">
        <div className="flex-grow-1">
          <h4 className="m-0 page-main-title">Create User</h4>
        </div>
        <Link to={ROUTES.ACCOUNT_USERS} className="btn btn-outline-secondary btn-sm">
          &larr; Back to Users
        </Link>
      </div>

      <div className="row">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">User Details</h5>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card-body p-4">
                {message && (
                  <div
                    className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"}`}
                    role="alert"
                  >
                    {message.text}
                  </div>
                )}

                <div className="col-12 mb-4 text-center">
                  <label className="form-label fw-medium text-green d-block mb-3">Profile Photo</label>
                  <div className="d-inline-block position-relative">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
                      style={{
                        height: 110,
                        width: 110,
                        border: "4px solid #0e4d52",
                        background: "#123840",
                        color: "#fff",
                        fontSize: "2.5rem",
                        fontWeight: 700
                      }}
                    >
                      {initials}
                    </span>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Name<span className="text-danger"> *</span>
                    </label>
                    <input
                      className="form-control"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="e.g. Jane Doe"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Mob No.</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value)}
                      placeholder="e.g. +900-1234567"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Email ID<span className="text-danger"> *</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. jane.doe@company.com"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Role<span className="text-danger"> *</span>
                    </label>
                    <select className="form-select" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                      <option value="">-- Select Role --</option>
                      {roles.map((r) => (
                        <option key={r.RoleID} value={r.RoleID}>
                          {r.RoleName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Department</label>
                    <select
                      className="form-select"
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                    >
                      <option value="">-- Select Department --</option>
                      {departments.map((d) => (
                        <option key={d.DepartmentID} value={d.DepartmentID}>
                          {d.DepartmentName}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Not in the list? Add it from <Link to={ROUTES.ACCOUNT_DEPARTMENTS}>Account &gt; Department</Link>.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Company</label>
                    <select className="form-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                      <option value="">-- Select Company --</option>
                      {companies.map((c) => (
                        <option key={c.CompanyID} value={c.CompanyID}>
                          {c.CompanyName}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Not in the list? Add it from <Link to={ROUTES.ACCOUNT_COMPANIES}>Account &gt; Company</Link>.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <ToggleSwitch checked={false} disabled label="Account Status" />
                    <div className="form-text mt-2">
                      New users always start as Inactive. Status becomes Active
                      automatically once the user opens the invite email and
                      creates their password.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <ToggleSwitch checked={isEnabled} onChange={setIsEnabled} label="Access Status" />
                    <div className="form-text mt-2">
                      You decide this, independent of Account Status -- a
                      Disabled user can never log in, even after they set a
                      password.
                    </div>
                  </div>
                </div>

                <div className="text-end mt-4">
                  <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
                    {submitting ? "Creating..." : "Create User"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Same custom-field/toggle-switch styling as EditUserForm.jsx /
          ProfilePage.jsx, scoped to this page. */}
      <style>{`
        .profile-toggle {
          position: relative;
          display: inline-block;
          width: 38px;
          height: 20px;
          margin: 0;
        }
        .profile-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .profile-toggle-slider {
          position: absolute;
          inset: 0;
          background-color: #ccc;
          transition: 0.2s;
          border-radius: 20px;
          cursor: pointer;
        }
        .profile-toggle-slider:before {
          content: "";
          position: absolute;
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: #fff;
          transition: 0.2s;
          border-radius: 50%;
        }
        .profile-toggle input:checked + .profile-toggle-slider {
          background-color: #124d52;
        }
        .profile-toggle input:checked + .profile-toggle-slider:before {
          transform: translateX(18px);
        }
        .profile-toggle-disabled .profile-toggle-slider {
          cursor: not-allowed;
          opacity: 0.55;
        }
      `}</style>
    </>
  );
}
